import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/use-toast';
import { Plus, Trash2, Edit2, Check, X } from 'lucide-react';

interface FieldOption {
  id: string;
  fieldName: string;
  value: string;
  isPredefined: boolean;
  colorValue?: string | null;
}

interface VenueTypeListingPrice {
  id: string;
  venueType: string;
  listingPriceCents: number;
}

export default function AdminFieldOptions() {
  const [availabilityTypes, setAvailabilityTypes] = useState<FieldOption[]>([]);
  const [venueTypeOptions, setVenueTypeOptions] = useState<FieldOption[]>([]);
  const [venueTypeListingPrices, setVenueTypeListingPrices] = useState<VenueTypeListingPrice[]>([]);
  const [venueTypes, setVenueTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [venueTypesLoading, setVenueTypesLoading] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [newVenueTypeValue, setNewVenueTypeValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingVenueTypeId, setEditingVenueTypeId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editVenueTypeValue, setEditVenueTypeValue] = useState('');
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editPriceValue, setEditPriceValue] = useState('');
  const [newVenueType, setNewVenueType] = useState('');
  const [newPriceValue, setNewPriceValue] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadAvailabilityTypes();
    loadVenueTypeOptions();
    loadVenueTypeListingPrices();
    loadVenueTypes();
  }, []);

  const loadAvailabilityTypes = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/field-options?fieldName=availabilityType', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAvailabilityTypes(data);
      }
    } catch (error) {
      console.error('Error loading availability types:', error);
      toast({
        title: 'Error',
        description: 'Failed to load availability types',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadVenueTypeListingPrices = async () => {
    try {
      const res = await fetch('/api/admin/venue-type-listing-prices', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setVenueTypeListingPrices(data);
      }
    } catch (error) {
      console.error('Error loading venue type listing prices:', error);
    }
  };

  const loadVenueTypeOptions = async () => {
    try {
      setVenueTypesLoading(true);
      const res = await fetch('/api/admin/field-options?fieldName=venueType', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setVenueTypeOptions(data);
      }
    } catch (error) {
      console.error('Error loading venue type options:', error);
      toast({
        title: 'Error',
        description: 'Failed to load venue types',
        variant: 'destructive',
      });
    } finally {
      setVenueTypesLoading(false);
    }
  };

  const loadVenueTypes = async () => {
    try {
      const res = await fetch('/api/admin/machines/field-options/venueType', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const venueTypeValues = data.map((opt: FieldOption) => opt.value).filter(Boolean);
        setVenueTypes(venueTypeValues);
      } else {
        console.error('Failed to load venue types:', res.status);
        // Fallback: try loading from field-options endpoint
        const fallbackRes = await fetch('/api/admin/field-options?fieldName=venueType', {
          credentials: 'include',
        });
        if (fallbackRes.ok) {
          const fallbackData = await fallbackRes.json();
          const venueTypeValues = fallbackData.map((opt: FieldOption) => opt.value).filter(Boolean);
          setVenueTypes(venueTypeValues);
        }
      }
    } catch (error) {
      console.error('Error loading venue types:', error);
    }
  };

  const handleCreate = async () => {
    if (!newValue.trim()) return;

    try {
      const res = await fetch('/api/admin/field-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fieldName: 'availabilityType',
          value: newValue.trim(),
        }),
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Availability type created',
        });
        setNewValue('');
        loadAvailabilityTypes();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to create availability type',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating availability type:', error);
      toast({
        title: 'Error',
        description: 'Failed to create availability type',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editValue.trim()) return;

    try {
      const res = await fetch(`/api/admin/field-options/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          value: editValue.trim(),
        }),
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Availability type updated',
        });
        setEditingId(null);
        setEditValue('');
        loadAvailabilityTypes();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to update availability type',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating availability type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update availability type',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string, value: string) => {
    if (!window.confirm(`Are you sure you want to delete "${value}"? This will affect all machines using this availability type.`)) {
      return;
    }

    try {
      const res = await fetch(`/api/admin/field-options/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Availability type deleted',
        });
        loadAvailabilityTypes();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to delete availability type',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting availability type:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete availability type',
        variant: 'destructive',
      });
    }
  };

  const startEditing = (option: FieldOption) => {
    setEditingId(option.id);
    setEditValue(option.value);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleCreateVenueType = async () => {
    if (!newVenueTypeValue.trim()) return;

    try {
      const res = await fetch('/api/admin/field-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fieldName: 'venueType',
          value: newVenueTypeValue.trim(),
        }),
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Venue type created',
        });
        setNewVenueTypeValue('');
        loadVenueTypeOptions();
        loadVenueTypes();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to create venue type',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating venue type:', error);
      toast({
        title: 'Error',
        description: 'Failed to create venue type',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateVenueType = async (id: string) => {
    if (!editVenueTypeValue.trim()) return;

    try {
      const res = await fetch(`/api/admin/field-options/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          value: editVenueTypeValue.trim(),
        }),
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Venue type updated',
        });
        setEditingVenueTypeId(null);
        setEditVenueTypeValue('');
        loadVenueTypeOptions();
        loadVenueTypes();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to update venue type',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating venue type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update venue type',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteVenueType = async (id: string, value: string) => {
    if (!window.confirm(`Are you sure you want to delete "${value}"? This will affect all machines using this venue type and may cause issues with pricing multipliers.`)) {
      return;
    }

    try {
      const res = await fetch(`/api/admin/field-options/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Venue type deleted',
        });
        loadVenueTypeOptions();
        loadVenueTypes();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to delete venue type',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting venue type:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete venue type',
        variant: 'destructive',
      });
    }
  };

  const startEditingVenueType = (option: FieldOption) => {
    setEditingVenueTypeId(option.id);
    setEditVenueTypeValue(option.value);
  };

  const cancelEditingVenueType = () => {
    setEditingVenueTypeId(null);
    setEditVenueTypeValue('');
  };

  const handleCreateListingPrice = async () => {
    if (!newVenueType.trim() || !newPriceValue.trim()) return;

    const priceCents = Math.round(parseFloat(newPriceValue) * 100);
    if (isNaN(priceCents) || priceCents < 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid price',
        variant: 'destructive',
      });
      return;
    }

    try {
      const res = await fetch('/api/admin/venue-type-listing-prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          venueType: newVenueType.trim(),
          listingPriceCents: priceCents,
        }),
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Venue type listing price created',
        });
        setNewVenueType('');
        setNewPriceValue('');
        loadVenueTypeListingPrices();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to create listing price',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating listing price:', error);
      toast({
        title: 'Error',
        description: 'Failed to create listing price',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateListingPrice = async (id: string) => {
    if (!editPriceValue.trim()) return;

    const priceCents = Math.round(parseFloat(editPriceValue) * 100);
    if (isNaN(priceCents) || priceCents < 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid price',
        variant: 'destructive',
      });
      return;
    }

    try {
      const res = await fetch(`/api/admin/venue-type-listing-prices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          listingPriceCents: priceCents,
        }),
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Listing price updated',
        });
        setEditingPriceId(null);
        setEditPriceValue('');
        loadVenueTypeListingPrices();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to update listing price',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating listing price:', error);
      toast({
        title: 'Error',
        description: 'Failed to update listing price',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteListingPrice = async (id: string, venueType: string) => {
    if (!window.confirm(`Are you sure you want to delete the listing price for "${venueType}"?`)) {
      return;
    }

    try {
      const res = await fetch(`/api/admin/venue-type-listing-prices/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        toast({
          title: 'Success',
          description: 'Listing price deleted',
        });
        loadVenueTypeListingPrices();
      } else {
        const error = await res.json();
        toast({
          title: 'Error',
          description: error.error || 'Failed to delete listing price',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting listing price:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete listing price',
        variant: 'destructive',
      });
    }
  };

  const startEditingPrice = (price: VenueTypeListingPrice) => {
    setEditingPriceId(price.id);
    setEditPriceValue((price.listingPriceCents / 100).toFixed(2));
  };

  const cancelEditingPrice = () => {
    setEditingPriceId(null);
    setEditPriceValue('');
  };

  return (
    <div className="h-full bg-gray-50">
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Availability Types</h1>
          <p className="text-gray-600 mt-2">
            Manage availability types for machines. Changes will affect all dropdowns and filters across the system.
          </p>
        </div>

        {/* Create New */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Add New Availability Type</CardTitle>
            <CardDescription>
              Create a new availability type that will be available for all machines
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="e.g., Campaign Machine"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreate();
                    }
                  }}
                />
              </div>
              <Button onClick={handleCreate} disabled={!newValue.trim()}>
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardHeader>
            <CardTitle>Existing Availability Types</CardTitle>
            <CardDescription>
              {availabilityTypes.length} availability type(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading...</p>
              </div>
            ) : availabilityTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No availability types found. Add one above to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {availabilityTypes.map((option) => (
                  <div
                    key={option.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                  >
                    {editingId === option.id ? (
                      <>
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 mr-3"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdate(option.id);
                            } else if (e.key === 'Escape') {
                              cancelEditing();
                            }
                          }}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleUpdate(option.id)}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1">
                          <span className="font-medium text-gray-900">{option.value}</span>
                          {option.isPredefined && (
                            <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              System
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEditing(option)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(option.id, option.value)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Important Notes:</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Changes take effect immediately across the entire system</li>
            <li>Deleting an availability type will not remove it from existing machines, but it won't appear in dropdowns</li>
            <li>Editing a value will update the dropdown options but not existing machine data</li>
          </ul>
        </div>

        {/* Venue Types Section */}
        <div className="mt-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Venue Types</h1>
            <p className="text-gray-600 mt-2">
              Manage venue types for machines. New venue types will appear in the Retail Media Pricing multipliers table.
            </p>
          </div>

          {/* Create New */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Add New Venue Type</CardTitle>
              <CardDescription>
                Create a new venue type that will be available for machines and pricing multipliers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    placeholder="e.g., Entertainment, Education"
                    value={newVenueTypeValue}
                    onChange={(e) => setNewVenueTypeValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateVenueType();
                      }
                    }}
                  />
                </div>
                <Button onClick={handleCreateVenueType} disabled={!newVenueTypeValue.trim()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* List */}
          <Card>
            <CardHeader>
              <CardTitle>Existing Venue Types</CardTitle>
              <CardDescription>
                {venueTypeOptions.length} venue type(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {venueTypesLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading...</p>
                </div>
              ) : venueTypeOptions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No venue types found. Add one above to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {venueTypeOptions.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      {editingVenueTypeId === option.id ? (
                        <>
                          <Input
                            value={editVenueTypeValue}
                            onChange={(e) => setEditVenueTypeValue(e.target.value)}
                            className="flex-1 mr-3"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateVenueType(option.id);
                              } else if (e.key === 'Escape') {
                                cancelEditingVenueType();
                              }
                            }}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleUpdateVenueType(option.id)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditingVenueType}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{option.value}</span>
                            {option.isPredefined && (
                              <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                System
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditingVenueType(option)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteVenueType(option.id, option.value)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Important Notes:</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>New venue types will automatically appear in the Retail Media Pricing multipliers table</li>
              <li>Deleting a venue type will not remove it from existing machines, but it won't appear in dropdowns</li>
              <li>Editing a venue type will update dropdowns but not existing machine data</li>
            </ul>
          </div>
        </div>

        {/* Venue Type Listing Prices Section */}
        <div className="mt-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Venue Type Listing Prices</h1>
            <p className="text-gray-600 mt-2">
              Set the listing price per SKU for each venue type. This price applies to 4-12 week campaigns.
            </p>
          </div>

          {/* Create New */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Add New Listing Price</CardTitle>
              <CardDescription>
                Set the listing price for a venue type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="venue-type-select" className="mb-2 block">Venue Type</Label>
                  <select
                    id="venue-type-select"
                    value={newVenueType}
                    onChange={(e) => setNewVenueType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select venue type...</option>
                    {venueTypes
                      .filter(vt => !venueTypeListingPrices.some(p => p.venueType === vt))
                      .map(vt => (
                        <option key={vt} value={vt}>{vt}</option>
                      ))}
                  </select>
                </div>
                <div className="flex-1">
                  <Label htmlFor="price-input" className="mb-2 block">Price Per SKU (£)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                    <Input
                      id="price-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newPriceValue}
                      onChange={(e) => setNewPriceValue(e.target.value)}
                      className="pl-8"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCreateListingPrice();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleCreateListingPrice} disabled={!newVenueType.trim() || !newPriceValue.trim()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* List */}
          <Card>
            <CardHeader>
              <CardTitle>Existing Listing Prices</CardTitle>
              <CardDescription>
                {venueTypeListingPrices.length} venue type(s) with listing prices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {venueTypeListingPrices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No listing prices set. Add one above to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {venueTypeListingPrices.map((price) => (
                    <div
                      key={price.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      {editingPriceId === price.id ? (
                        <>
                          <div className="flex-1 font-medium text-gray-900">{price.venueType}</div>
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={editPriceValue}
                                onChange={(e) => setEditPriceValue(e.target.value)}
                                className="pl-8 w-32"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleUpdateListingPrice(price.id);
                                  } else if (e.key === 'Escape') {
                                    cancelEditingPrice();
                                  }
                                }}
                                autoFocus
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleUpdateListingPrice(price.id)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEditingPrice}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{price.venueType}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-lg font-semibold text-gray-900">
                              £{(price.listingPriceCents / 100).toFixed(2)}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEditingPrice(price)}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteListingPrice(price.id, price.venueType)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

